import React, { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { thaiRoute } from 'lib/dictionary-routes'
import { ThaiWordBasicProps } from 'core/interfaces/dictionary-thai-interfaces'
import { useTranslit } from 'hooks/useTranslit'
import { availableVoices, playSound } from '../../../lib/sounds'
import { markSearchSource } from 'lib/analytics/events'
import ThaiWordListMeaning from './list/thai-word-list-meaning'
import CommonnessChip from '../commonness-chip'
import ListenControl from '../listen-control'
import { usePartHover } from './part-hover-context'

type Props = {
   // The compound's constituent parts (>= 2), shown as the "Components" section:
   // one detail row per part, each lighting its chunk in the hero headword on
   // hover (see part-hover-context).
   parts: ThaiWordBasicProps[]
   // Related / example words built from the headword, shown stacked below as the
   // "Built up from <headword>" section.
   relatedWords: ThaiWordBasicProps[]
   // The page's headword, named in the related-words section label.
   headword: string
   // When the sense has no example sentences the panel spans the full page
   // width; lay the related words out in two columns to use it.
   wide?: boolean
}

// Violet / green categorical palette, cycled per part — the same slots the seg
// chips and speaker icons share elsewhere.
const partClass = (index: number): string =>
   index % 2 === 0
      ? 'hover:!text-part1 focus-visible:!text-part1'
      : 'hover:!text-part2 focus-visible:!text-part2'

// Related lists are commonness-sorted server-side, and very common words carry
// huge tails of obscure entries — cap the first render and reveal the rest.
const INITIAL_LIMIT = 8

const SectionLabel = ({ title, count }: { title: React.ReactNode; count: number }) => (
   <div className="flex items-center gap-[9px] mb-[6px]">
      <span className="text-[13px] font-bold tracking-[1.2px] uppercase text-textMuted">
         {title}
      </span>
      <span className="text-[11px] font-bold bg-chipBg border border-chipBorder text-textMuted rounded-full px-[8px] py-[2px]">
         {count}
      </span>
   </div>
)

// One word as a flat, hairline-separated row: headword + transliteration and
// meaning on the left, a commonness meter and speaker on the right. `accent`
// tints the speaker to the part's palette slot, and `partIndex` (Components
// only) lights that chunk up inside the hero headword while the row is hovered.
const DetailRow = ({
   word,
   accent,
   partIndex,
}: {
   word: ThaiWordBasicProps
   accent?: string
   partIndex?: number
}) => {
   const { setHoveredPart } = usePartHover()
   const translit = useTranslit().of(word.t2e)

   // Only ~10k of the 120k Thai entries have a recorded clip, so the speaker is
   // earned, not assumed: no thai_audio_files row (and no legacy flag) means no
   // button at all, rather than one that plays a 404. ListenControl still holds
   // the slot open in that case, so the commonness chips stay in one column.
   const voices = availableVoices(word.audioGenders, word.hasSoundFile)

   const hoverProps =
      partIndex === undefined
         ? {}
         : {
              onMouseEnter: () => setHoveredPart(partIndex),
              onMouseLeave: () => setHoveredPart(null),
              onFocus: () => setHoveredPart(partIndex),
              onBlur: () => setHoveredPart(null),
           }

   return (
      <div
         className="flex items-start gap-[16px] py-[18px] border-t border-t-hairline"
         {...hoverProps}
      >
         <Link
            {...thaiRoute(word.word)}
            className="flex-1 min-w-0 block no-underline text-inherit"
            onClick={() => markSearchSource('result_click')}
         >
            <div className="flex items-baseline flex-wrap gap-[10px]">
               <span className="text-[26px] text-thaiWord leading-[1.3] thai">{word.word}</span>
               {translit && (
                  <span className="font-translit text-[16px] italic text-translit">{translit}</span>
               )}
            </div>
            <div className="mt-[3px] text-[16px] text-bodyGray">
               {word.meanings.map((meaning, index) => (
                  <ThaiWordListMeaning
                     key={index}
                     meaning={meaning}
                     engTranslationWords={undefined}
                  />
               ))}
            </div>
         </Link>
         {/* Fixed to the height of the headword line so the chip and the
             (taller) 44px play target both centre on it, however many lines the
             meaning below runs to. */}
         <div className="flex items-center gap-[12px] shrink-0 h-[34px]">
            {word.commonnessScore != null && (
               <CommonnessChip commonnessScore={word.commonnessScore} display="meter" />
            )}
            <ListenControl
               voices={voices}
               subject={word.word}
               accentClass={accent}
               onPlay={(gender) => {
                  void playSound(word.word, gender)
               }}
            />
         </div>
      </div>
   )
}

// The left column of a sense: the compound's Components stacked above its
// Related words. Replaces the earlier tabbed module — both sections are now
// visible at once, each a labelled list of detail rows.
const WordBreakdownPanel = ({ parts, relatedWords, headword, wide }: Props) => {
   const [expanded, setExpanded] = useState(false)

   const hasParts = parts.length >= 2
   const hasRelated = relatedWords.length > 0
   if (!hasParts && !hasRelated) {
      return null
   }

   const isTruncated = relatedWords.length > INITIAL_LIMIT
   const visibleRelated = expanded ? relatedWords : relatedWords.slice(0, INITIAL_LIMIT)
   const hiddenCount = relatedWords.length - INITIAL_LIMIT

   return (
      <div className="min-w-0">
         {hasParts && (
            <section className="min-w-0">
               <SectionLabel title="Components" count={parts.length} />
               {/* Same two-column flow as the related words when the panel has
                   the full page width to itself. */}
               <div
                  className={`flex flex-col ${wide ? 'grid grid-cols-1 min-[861px]:grid-cols-2 gap-x-[44px]' : ''}`}
               >
                  {parts.map((part, index) => (
                     <DetailRow
                        key={index}
                        word={part}
                        accent={partClass(index)}
                        partIndex={index}
                     />
                  ))}
               </div>
            </section>
         )}

         {hasRelated && (
            <section className={`min-w-0 ${hasParts ? 'mt-[32px]' : ''}`}>
               <SectionLabel
                  title={
                     <>
                        Built up from{' '}
                        <span className="tracking-normal normal-case text-thaiWord text-[16px] thai">
                           {headword}
                        </span>
                     </>
                  }
                  count={relatedWords.length}
               />
               <div
                  className={`flex flex-col ${wide ? 'grid grid-cols-1 min-[861px]:grid-cols-2 gap-x-[44px]' : ''}`}
               >
                  {visibleRelated.map((word, index) => (
                     <DetailRow key={index} word={word} />
                  ))}
               </div>
               {isTruncated && (
                  <button
                     type="button"
                     className="bg-transparent border-none pt-[12px] px-0 pb-0 m-0 font-inherit text-[14px] font-bold text-translit cursor-pointer hover:underline"
                     onClick={() => setExpanded((prev) => !prev)}
                  >
                     {expanded ? 'Show fewer' : `Show ${hiddenCount} more`}
                  </button>
               )}
            </section>
         )}
      </div>
   )
}

export default WordBreakdownPanel
